// src/pages/Register.js
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { functions } from '../firebase/config';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteStatus, setInviteStatus] = useState('loading'); // loading | valid | invalid
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const inviteToken = searchParams.get('invite');
  const inviteTeamName = searchParams.get('team');

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    async function loadInvite() {
      try {
        const getInvitePreview = httpsCallable(functions, 'getInvitePreview');
        const result = await getInvitePreview({ token: inviteToken });
        if (cancelled) return;
        setInvitePreview(result.data);
        setEmail(result.data.email || '');
        setInviteStatus('valid');
      } catch (err) {
        if (cancelled) return;
        setInviteStatus('invalid');
      }
    }
    loadInvite();
    return () => { cancelled = true; };
  }, [inviteToken]);

  // Registration is invite/payment-gated — no invite token means no valid path
  // to an account (Pricing.js's Solo/Team buttons both route through Stripe,
  // which is what mints the invite token).
  if (!inviteToken) {
    return <Navigate to="/pricing" replace />;
  }

  const teamName = invitePreview?.teamName || inviteTeamName;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError('Passwords do not match.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, inviteToken);
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-hero">
        <img src="/logo-white.png" alt="Meeting.Training" className="auth-hero-logo" />
        <h1>Meetings Don't <br />Have to Be <br /><em>Boring or<br />Unproductive.</em></h1>
        <p>Great meetings lead to better and faster decisions.</p>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-box fade-up">
          <h2>{teamName || 'Create account'}</h2>
          <p className="subtitle">
            {teamName
              ? "You've been invited to assess your team meetings. It's a brief set of behaviors and roles along with your anonymous observations. Results will be shared with the entire team once completed."
              : "You've been invited — complete your profile below."}
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          {inviteStatus === 'loading' && (
            <p style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
              <span className="spinner" /> Checking your invitation…
            </p>
          )}

          {inviteStatus === 'invalid' && (
            <div className="alert alert-error">
              This invitation link is invalid or has already been used. Contact whoever invited you for a new link.
            </div>
          )}

          {inviteStatus === 'valid' && (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" required />
              </div>
              <div className="form-group">
                <label>Email address</label>
                <input type="email" value={email} readOnly disabled title="This invitation was sent to this address" />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" required />
              </div>
              <div className="form-group">
                <label>Confirm password</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••" required />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? <><span className="spinner" /> Creating account...</> : 'START ASSESSMENT →'}
              </button>
            </form>
          )}

          <hr className="divider" />

          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" className="link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}